import * as actions from './actions';
import axios from 'axios';
import _ from 'underscore';

import { buildTrace, buildStatsPlot, buildMultiStatsPlot, buildStatYear, arrayToObject, findMissingYears } from '../../utilities/index';

const STAGE_COLORS = {
  major: '#cc33ff61',
  moderate: '#ff000061',
  flood: '#ff990061',
  action: '#ffff0061',
  low: '#90632061'
};

function getMinMax (locations, type, source, river) {
  return axios.get(`/api/query/range/${locations}/${type}/${source}/${river}`).then(({data}) => data).catch(err => ({success: false}))
};

function getStages (locations, type) {
  return axios.get(`/api/query/stage/${locations}/${type}`).then(({data}) => data).catch(err => ({success: false}))
};

function getTraces (source, river, locations, type, alternatives, years) {
  return axios.get(`/api/query/apply/traces/${source}/${river}/${locations}/${type}/${alternatives}/${years}`).then(({data}) => data).catch(err => ({success: false}))
};

function getThresholds (locations, type) {
  return axios.get(`/api/query/thresholds/${locations[0]}/${type}`).then(({data}) => data).catch(err => ({success: false}))
};

function getUserThresholds (location_id, type_id, user_id) {
  return axios.get(`/api/query/user-thresholds/${location_id}/${type_id}/${user_id}`).then(({data}) => data).catch(err => ({success: false}))
}

function getInitialTraces (source, river, locations, type, pickYears) {
  return axios.get(`/api/query/apply/multi-alt/${source}/${river}/${locations[0]}/${type}/${pickYears}`).then(({data}) => data).catch(err => ({success: false}))
};

function getStats (source, river, locations, type) {
  return axios.get(`/api/query/apply/stats/${source}/${river}/${locations[0]}/${type}`).then(({data}) => data).catch(err => ({success: false}))
};

function getTimeseries (source, river, locations, type) {
  // return axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/${startDate}/${endDate}/${stats || 0}/${ninetieth || 0}/${tenth || 0}/${fiftieth || 0}/${min || 0}/${max || 0}/${average || 0}`).then(({data}) => data).catch(err => ({success: false}))
  return axios.all([
    axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/1931/1938`).then(({data}) => data).catch(err => ({success: false})),
    axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/1939/1946`).then(({data}) => data).catch(err => ({success: false})),
    axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/1947/1954`).then(({data}) => data).catch(err => ({success: false})),
    axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/1955/1962`).then(({data}) => data).catch(err => ({success: false})),
    axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/1963/1970`).then(({data}) => data).catch(err => ({success: false})),
    axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/1971/1978`).then(({data}) => data).catch(err => ({success: false})),
    axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/1979/1985`).then(({data}) => data).catch(err => ({success: false})),
    axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/1986/1993`).then(({data}) => data).catch(err => ({success: false})),
    axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/1994/2001`).then(({data}) => data).catch(err => ({success: false})),
    axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/2002/2009`).then(({data}) => data).catch(err => ({success: false})),
    axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/2010/2012`).then(({data}) => data).catch(err => ({success: false})),
    // axios.get(`/api/query/apply/time-series/${source}/${river}/${locations}/${type}/2002/2012`).then(({data}) => data).catch(err => ({success: false}))
  ])
  .then(axios.spread((first, second, third, fourth, fifth, sixth, seventh, eighth, nineth, tenth, eleventh) => {
    if(first.success && second.success && third.success && fourth.success && fifth.success && sixth.success && seventh.success && eighth.success && nineth.success && tenth.success && eleventh.success){
      return {
        success: true,
        result: [
          ...first.result,
          ...second.result,
          ...third.result,
          ...fourth.result,
          ...fifth.result,
          ...sixth.result,
          ...seventh.result,
          ...eighth.result,
          ...nineth.result,
          ...tenth.result,
          ...eleventh.result
        ]
      };
    } else {
      return {
        success: false,
        result: []
      }
    }
  }))
};

const saveThresholdToDatabase = async (threshold) => {
  return await axios.post('/api/query/user-thresholds/save', {threshold})
};

const updateThresholdInDatabase = async (threshold) => {
  return await axios.put('/api/query/user-thresholds/update', {threshold})
};

const removeThresholdFromDatabse = async (threshold) => {
  return await axios.put('/api/query/user-thresholds/delete', {threshold})
};

export const setLastApplied = (filters, index) => ({
  type: actions.MULTI_SET_LAST_APPLIED,
  filters,
  index
});

// async actions
export const getFilters = (user) => {
  return (dispatch) => {
    axios.all([
      axios.get('/api/query/alternatives'),
      axios.get('/api/query/locations'),
      axios.get('/api/query/types'),
      axios.get('/api/query/sources'),
      axios.get('/api/query/rivers'),
      axios.get('/api/query/favorites'),
      axios.get(`/api/query/user-favorites/${user.id}`)
    ])
    .then(axios.spread((alternatives, locations, types, sources, rivers, favorites, userFavorites) => {
      const arrA = favorites.data.favorites.map(item => ({...item, group: 'Key Locations'}));
      const arrB = userFavorites.data.userFavorites.map(item => ({...item, group: 'My Favorites'}));
      // const arrC = arrA.concat(arrB);
      const filters = {
        alternatives: alternatives.data.alternatives,
        locations: locations.data.locations,
        types: types.data.types,
        sources: sources.data.sources,
        rivers: rivers.data.rivers,
        favorites: arrA,
        userFavorites: arrB
        // userFavorites: userFavorites.data.userFavorites.map(item => ({...item, group: 'My Favorites'}))
      };
      dispatch(setFilters(filters))
    }))
    .catch(err => {
      dispatch(setError('Could not generate filters'))
    });
  }
};

export const loadYears = (filters, index) => {
  return (dispatch, getState) => {
    const { 
      locations, // array of locations, 1 or many
      alternatives, // array of locations, 1 or many
      type, // a single type id
      source, // a single source id
      river,  // a single river id
      startDate, // a start date as year e.g 2000
      method, // the method to fetch data from, e.g. 'Stats' 'Multi Alternative'
      selectedStat,
      pickYear,
      selectedAlts,
    } = filters;



    const currentYears = getState().multi.plots[index].bulkData ? Object.keys(getState().multi.plots[index].bulkData) : [];
    let pickYears;
    switch(method){
      case 'Multiple Alternative':
          if(!source || !river || !locations || (locations && !locations.length) || !type){
            return null;
          }
          pickYears = findMissingYears(currentYears, pickYear);
          dispatch(setChartLoading(true, index));
          return axios.all([getInitialTraces(source, river, locations, type, pickYears), getStats(source, river, locations, type)])
          .then(axios.spread((traceData, stats) => {
            if(traceData.success && stats.success){
              const alts = _.groupBy(getState().multi.filters.alternatives, 'alternative_id');
              const result = _.groupBy(traceData.result.map(d => ({...d, short_name: alts[d.id][0].short_name, color: alts[d.id][0].color })), 'year');
              const statsResult =_.groupBy(stats.result, 'short_name');
              const grouped = Object.keys(result).map(year => {
                let annualData = _.groupBy(result[year], 'short_name');
                return {
                  [year]: [].concat(...Object.keys(annualData).map(alt => {
                    let stats = statsResult[alt];
                    let merged = annualData[alt].map((data, index) => ({...data, ...stats[index]}))
                    return merged
                  }))
                }
              })
              const reduced = arrayToObject(grouped);
              dispatch(setBulkStats(reduced, index));
              const plotData = buildStatYear(reduced, selectedStat || 'maximum', pickYear || 2012, selectedAlts); // apply max stat and apply all alts by default
              dispatch(setPlotData(plotData, index)); // set the plot data at the right index
              dispatch(setChartLoading(false, index)); // clear loading state from cahrt
            } else {
              dispatch(setChartLoading(false, index));
              dispatch(setError('Unable to complete query'));
            };
          }))
          .catch(err => {
            dispatch(setChartLoading(false, index));
            dispatch(setError('Unable to complete query'));
          })
      case 'Single Alternative':
        if(!source || !river || !locations || (locations && !locations.length) || !type || (!alternatives || (alternatives && !alternatives.length))){
          return null;
        }
        pickYears = findMissingYears(currentYears, startDate);
        dispatch(setChartLoading(true, index));
        return getTraces(source, river, locations, type, alternatives, pickYears)
        .then(traceData => {
          
          if(traceData.success){
            const result = _.groupBy(traceData.result, 'year');
            if(!result){
              dispatch(setPlotData([], index))
            } else {
              dispatch(setBulkStats(result, index));
              const plotData = buildStatsPlot(result, startDate, index); // takes the raw data and formats it for plotly
              dispatch(setChartLoading(false, index));
              dispatch(setPlotData(plotData, index));
            };
          } else {
            dispatch(setError(traceData.error_message || 'There was an issue processing your request'));
          };
          dispatch(setChartLoading(false, index)); // clear loading state from cahrt
        })
        .catch(err => {
          dispatch(setChartLoading(false, index));
          dispatch(setError('Unable to complete query'));
        })
      case 'Time Series':
        break;
      default: break;
    }
  }
}

export const applyFilters = (filters, index, pickedAlts) => {
  return (dispatch, getState) => {
    const { 
      locations, // array of locations, 1 or many
      alternatives, // array of locations, 1 or many
      type, // a single type id
      source, // a single source id
      river,  // a single river id
      startDate, // a start date as year e.g 2000
      // endDate,
      method, // the method to fetch data from, e.g. 'Stats' 'Multi Alternative'
      stats, // boolean, should display ALL statistics (only applicable to multi alt / multi location)
      selectedStat,
      pickYear,
      ninetieth, // boolean, should show the 90th percentile stat?
      tenth, // boolean, should show the 10th percentile?
      fiftieth, // boolean, should show the 50th percentile?
      min, // boolean, should show the min?
      max, // boolean, should show the max?
      average, // boolean, should show the average,
      selectedAlts
    } = filters;

    const prevSelectedAlts = {...selectedAlts};
    
    if(!method){ return dispatch(setError('You did not select a chart method')) };
    
    dispatch(setLastApplied({method, source, river, locations, type, alternatives}, index))

    const user_id = getState().auth.user.id;
    const currentYears = getState().multi.plots[index].bulkData ? Object.keys(getState().multi.plots[index].bulkData) : [];
    const pickYears = findMissingYears(currentYears, pickYear || 2012);

    if(method === 'Multiple Alternative'){
      if(!source){ return dispatch(setError('You did not select a source')) };
      if(!river){ return dispatch(setError('You did not select a river')) };
      if(!locations){ return dispatch(setError('You did not select a location')) };
      if(!type){ return dispatch(setError('You did not select a type')) };

      dispatch(setChartLoading(true, index));
      dispatch(clearThresholds(index));
      dispatch(clearStages(index));

      axios.all([getMinMax(locations, type, source, river), getStages(locations, type), getThresholds(locations, type), getUserThresholds(locations[0], type, user_id), getInitialTraces(source, river, locations, type, pickYears), getStats(source, river, locations, type)])
      .then(axios.spread((minMax, stageData, thresholdData, userThresholds, traceData, stats) => {
        let min, max;
        // define and the min max values
        if(minMax.success){
          min = parseFloat(minMax.min);
          max = parseFloat(minMax.max);
        }; 
        // if we have stage data, check if the the major or low thresholds are higher or lower than the min max.
        if(stageData.success){
          const { stages } = stageData;
          if(stages.major){
            if(parseFloat(stages.major) > parseFloat(max)) max = parseFloat(stages.major);
          }
          if(stages.low){
            if(parseFloat(stages.low) < parseFloat(min)) min = parseFloat(stages.low);
          }
        };
        // calcualte a buffer around the range;
        let range = max - min;
        let buffer = range / 10;
        let bufMax = max + buffer;
        let bufMin = min - buffer;
        // set the chart y axis range and fix it so it cannot change.
        dispatch(editChartY({range: [bufMin, bufMax]}, index));


        // if we have thresholdData
        if(thresholdData.success){
          thresholdData.thresholds.forEach(threshold => {
            dispatch(addThreshold(index, {...threshold, locked: true}))
          })
        };

        // if we have user saved thresholds
        if(userThresholds.success){
          userThresholds.thresholds.forEach(threshold => {
            dispatch(addThreshold(index, {...threshold, saved: true}))
          })
        };

        // if we have stageData
        if(stageData.success){
          const { stages, datum } = stageData;
          if(datum) {
            dispatch(toggleDatum(true, index))
            dispatch(editDatum(datum, index))
          };
          // add a threshold at the buffered max to allow fill property to work.
          dispatch(addStage(index, { key: 'stage-max', name: 'Chart boundary max', hoverinfo: 'none', value: bufMax, width: 0, showlegend: false})) // this will make the line hidden but still act as a boundary
          
          Object.keys(stages).forEach(stage => {
            stages[stage] && dispatch(addStage(index, { key: `stage-${stage}`, hoverinfo: 'none', name: stage, value: stages[stage], color: STAGE_COLORS[stage], fill: (stage !== 'low') && 'tonexty', width: 0 }));
          });
          // add a threshold at the beffered min to allow fill property to work.
          dispatch(addStage(index, { key: 'stage-min', name: 'Chart boundary min', hoverinfo: 'none', value: bufMin, width: 0, color: STAGE_COLORS.low, fill: stages.low && 'tonexty', showlegend: false})) // this will make the line hidden but still act as a boundary
        } else {
          // no datum
          dispatch(toggleDatum(false, index))
          dispatch(editDatum(null, index))
        };
        if(traceData.success && stats.success){
          const alts = _.groupBy(getState().multi.filters.alternatives, 'alternative_id');
          const result = _.groupBy(traceData.result.map(d => ({...d, short_name: alts[d.id][0].short_name, color: alts[d.id][0].color })), 'year');
          const statsResult =_.groupBy(stats.result, 'short_name');
          const grouped = Object.keys(result).map(year => {
            let annualData = _.groupBy(result[year], 'short_name');
            return {
              [year]: [].concat(...Object.keys(annualData).map(alt => {
                let stats = statsResult[alt];
                let merged = annualData[alt].map((data, index) => ({...data, ...stats[index]}))
                return merged
              }))
            }
          })
          const reduced = arrayToObject(grouped);
          dispatch(setBulkStats(reduced, index));
          const selectedAlts = _.groupBy(reduced[pickYear || 2012], 'short_name'); // default to 2012
          Object.keys(selectedAlts).forEach(key => {
            if(prevSelectedAlts && Object.keys(prevSelectedAlts).length){
              selectedAlts[key] = prevSelectedAlts[key]
            } else {
              selectedAlts[key] = true;
            }
          });
          dispatch(changeFilter({selectedAlts}, index)); // apply all alternatives by default
          const plotData = buildStatYear(reduced, selectedStat || 'maximum', pickYear || 2012, selectedAlts); // apply max stat and apply all alts by default
          dispatch(setPlotData(plotData, index)); // set the plot data at the right index
          dispatch(setChartLoading(false, index)); // clear loading state from cahrt
        } else {
          dispatch(setChartLoading(false, index));
          dispatch(setError('Unable to complete query'));
        };
      }))
      .catch(err => {
        dispatch(setChartLoading(false, index));
        dispatch(setError('Unable to complete query'));
      })
    } else {

      if(!source){ return dispatch(setError('You did not select a source')) };
      if(!river){ return dispatch(setError('You did not select a river')) };
      if(!locations){ return dispatch(setError('You did not select a location')) };
      if(!type){ return dispatch(setError('You did not select a type')) };
  
      let usingStats = stats || ninetieth || tenth || fiftieth || min || max || average;
  
      let statsFilter = { ninetieth, tenth, fiftieth, min, max, average };
      
      if(method === 'Single Alternative'){
        if(isNaN(startDate)){ return dispatch(setError('You did not select a year')) };
        if(!alternatives){ return dispatch(setError('You did not select an alternative')) };
        if(startDate > 2012 || startDate < 1931){ return dispatch(setError('Select a year from 1931 - 2012')) };

        dispatch(setChartLoading(true, index));
        dispatch(clearThresholds(index));
        dispatch(clearStages(index));
        

        axios.all([getMinMax(locations, type, source, river), getStages(locations, type), getTraces(source, river, locations, type, alternatives, startDate)])
        .then(axios.spread((minMax, stageData, traceData) => {
          let min, max;

          // if we have stageData
          if(stageData.success){
            const { datum } = stageData;
            if(datum) {
              dispatch(toggleDatum(true, index))
              dispatch(editDatum(datum, index))
            }
          }

          // define and the min max values
          if(minMax.success){
            min = parseFloat(minMax.min);
            max = parseFloat(minMax.max);
          };
          // calcualte a buffer around the range;
          let range = max - min;
          let buffer = range / 10;
          let bufMax = max + buffer;
          let bufMin = min - buffer;
          // set the chart y axis range and fix it so it cannot change.
          dispatch(editChartY({range: [bufMin, bufMax]}, index));
          
          if(traceData.success){
            const result = _.groupBy(traceData.result, 'year');
            if(!result){
              dispatch(setPlotData([], index))
            } else {
              dispatch(setBulkStats(result, index));
              const plotData = buildStatsPlot(result, startDate, index); // takes the raw data and formats it for plotly
              dispatch(setChartLoading(false, index));
              dispatch(setPlotData(plotData, index));
            };
          } else {
            dispatch(setError(traceData.error_message || 'There was an issue processing your request'));
          };
          dispatch(setChartLoading(false, index)); // clear loading state from cahrt
        }))
      } else if(method === 'Time Series'){
        if(!startDate){ return dispatch(setError('You did not specify a start date')) };

        dispatch(setChartLoading(true, index));
        dispatch(clearThresholds(index));
        dispatch(clearStages(index));

        axios.all([getMinMax(locations, type, source, river), getStages(locations, type), getThresholds(locations, type), getUserThresholds(locations[0], type, user_id), getTimeseries(source, river, locations, type)])
        .then(axios.spread((minMax, stageData, thresholdData, userThresholds, traceData) => {
          let min, max;
          // define and the min max values
          if(minMax.success){
            min = parseFloat(minMax.min);
            max = parseFloat(minMax.max);
          };
          // if we have stage data, check if the the major or low thresholds are higher or lower than the min max.
          if(stageData.success){
            const { stages } = stageData;
            if(stages.major){
              if(parseFloat(stages.major) > parseFloat(max)) max = parseFloat(stages.major);
            }
            if(stages.low){
              if(parseFloat(stages.low) < parseFloat(min)) min = parseFloat(stages.low);
            }
          };
          // calcualte a buffer around the range;
          let range = max - min;
          let buffer = range / 10;
          let bufMax = max + buffer;
          let bufMin = min - buffer;
          // set the chart y axis range and fix it so it cannot change.
          dispatch(editChartY({range: [bufMin, bufMax], fixedrange: false}, index));

          // if we have thresholdData
          if(thresholdData.success){
            thresholdData.thresholds.forEach(threshold => {
              dispatch(addThreshold(index, {...threshold, locked: true}))
            })
          };

          // if we have user thresholdData, add the saved flag
          if(userThresholds.success){
            userThresholds.thresholds.forEach(threshold => {
              dispatch(addThreshold(index, {...threshold, saved: true}))
            })
          };

          // if we have stageData
          if(stageData.success){
            const { stages, datum } = stageData;
            if(datum) {
              dispatch(toggleDatum(true, index))
              dispatch(editDatum(datum, index))
            };
            // add a threshold at the buffered max to allow fill property to work.
            dispatch(addStage(index, { key: 'stage-max', name: 'Chart boundary max', hoverinfo: 'none', value: bufMax, width: 0, showlegend: false})) // this will make the line hidden but still act as a boundary
            Object.keys(stages).forEach(stage => {
              stages[stage] && dispatch(addStage(index, { key: `stage-${stage}`, hoverinfo: 'none', name: stage, value: stages[stage], color: STAGE_COLORS[stage], fill: (stage !== 'low') && 'tonexty', width: 0}));
            });
            // add a threshold at the beffered min to allow fill property to work.
            dispatch(addStage(index, { key: 'stage-min', name: 'Chart boundary min', hoverinfo: 'none', value: bufMin, width: 0, color: STAGE_COLORS.low, fill: stages.low && 'tonexty', showlegend: false})) // this will make the line hidden but still act as a boundary
          } else {
            dispatch(toggleDatum(false, index))
            dispatch(editDatum(null, index))
          };
          if(traceData.success){
            const result = _.groupBy(traceData.result, 'short_name');
            if(!result) dispatch(setPlotData([], index));
            let selectedAlts = {};
            const plotData = Object.keys(result).map((key, index) => {
              selectedAlts[key] = true;
              if(usingStats){
                return buildMultiStatsPlot(result[key], index, statsFilter)
              } else {
                return buildTrace(result[key], index)
              }
            });
            dispatch(changeFilter({selectedAlts}, index)); // apply all alternatives by default
            if(usingStats){ 
              // plotData will be an array or arrays, need to combine them into one array
              const merged = [].concat.apply([], plotData);
              dispatch(setChartLoading(false, index));
              dispatch(setPlotData(merged, index))
            } else {
              // array of plot lines
              dispatch(setChartLoading(false, index));;
              dispatch(setPlotData(plotData, index));
            }
          };
          dispatch(setChartLoading(false, index)); // clear loading state from cahrt
        }))
      } else {
        dispatch(setChartLoading(false, index));
        dispatch(setError('No Method'));
      };
    };
    
  }
};

export const changeFilter = (changes, index, item) => {
  // receives changes as a object, the index of the changes, and the item to query
  return (dispatch, getState) => {
    dispatch(setAppliedFilters(changes, index)) // change the filter immediately
    // start an async request for a reduced list of available filters
    switch(item) {
      case 'favorite':
        // when we pick a favorite location, we need to simulate selecting a source, river, and location seperately
        axios.all([
          axios.get(`/api/query/find/rivers/${changes.source}`).then(({data}) => data).catch(err => ({success: false})),
          axios.get(`/api/query/find/locations/${changes.river}`).then(({data}) => data).catch(err => ({success: false})),
          axios.get(`/api/query/find/types/${changes.locations.join('&')}`).then(({data}) => data).catch(err => ({success: false}))
        ])
        .then(axios.spread((rivers, locations, types) => {
          if(types.success && rivers.success && locations.success){
            const availableFilters = {
              locations: locations.availableFilters.locations,
              rivers: rivers.availableFilters.rivers,
              types:  types.availableFilters.types
            };
            dispatch(setAvailableFilters(availableFilters, index))
          }
        }));
        break;
      case 'method':
        dispatch(setAvailableFilters({sources: getState().multi.filters.sources}, index));
        break;
      case 'source':
        // when we pick a source, we can determin the rivers we want to focus on
        axios.get(`/api/query/find/rivers/${changes.source}`)
        .then(result => {
          const { data } = result;
          if(!data.success){ dispatch(setError(data.error_message))} 
          else {
            dispatch(setAvailableFilters(data.availableFilters, index));
          }
        })
        .catch(error => {
          dispatch(setError('Something went wrong...'));
        })
      break;
      case 'river':
        // we also want to clear the map out for hover info
        dispatch(setMap(null));
        // when we pick a river, we can determin the locations we want to focus on
        axios.get(`/api/query/find/locations/${changes.river}`)
        .then(result => {
          const { data } = result;
          if(!data.success){ dispatch(setError(data.error_message))} 
          else {
            dispatch(setAvailableFilters(data.availableFilters, index));
          }
        })
        .catch(error => {
          dispatch(setError('Something went wrong...'));
        })
      break;
      case 'location':
        // when we pick a locatin, we can determin the types that are available
        axios.get(`/api/query/find/types/${changes.locations.join('&')}`)
        .then(result => {
          const { data } = result;
          if(!data.success){ dispatch(setError(data.error_message))} 
          else {
            dispatch(setAvailableFilters(data.availableFilters, index));
          }
        })
        .catch(error => {
          dispatch(setError('Something went wrong...'));
        })
      break;
      case 'type':
        // when we pick a type, we can see the alternatives that are avaialbe for that type
        // console.log(getState().multi.plots[index].appliedFilters);
        const { source, river, locations } = getState().multi.plots[index].appliedFilters;
        const { types } = getState().multi.filters;
        const { type } = changes;
        let axisText = types.filter(({type_id}) => type_id === type);
        if(axisText && axisText[0]){
          dispatch(editChartY({title: `${axisText[0].type} (${axisText[0].unit})`}))
        }
        axios.get(`/api/query/find/alternatives/${source}/${river}/${locations.join('&')}/${type}`)
        .then(result => {
          const { data } = result;
          if(!data.success){ dispatch(setError(data.error_message))} 
          else {
            dispatch(setAvailableFilters(data.availableFilters, index));
          }
        })
        .catch(error => {
          dispatch(setError('Something went wrong...'));
        })
      break;
      default:
      return 
    };
  };
};

export const findMap = (river) => {
  return (dispatch, getState) => {
    if(!river){
      return dispatch(setMap(null))
    }
    const { rivers } = getState().multi.filters;
    const found = rivers.map((riv) => (riv.river)).indexOf(river);
    if(found > -1){
      dispatch(setMap(rivers[found]))
    }
  }
};

export const toggleFavoriteLocation = (river_id, location_id, favorite) => {
  return (dispatch, getState) => {
    const user_id = getState().auth.user.id;
    if(river_id && location_id){
      if(favorite){
        // add the location and river to the user favorite list
        axios.post('/api/query/user-favorites/add', {river_id, location_id, user_id})
        .then(response => {
          if(response.data.success){
            return dispatch(getFilters(getState().auth.user));
          } else {
            alert('Unable to add to favorites')
          }
        })
        .catch(err => {
          alert('Unable to add to favorites')
        })
      } else {
        // remove the river and location from user favorite list
        axios.post('/api/query/user-favorites/remove', {river_id, location_id, user_id})
        .then(response => {
          if(response.data.success){
            return dispatch(getFilters(getState().auth.user));
          } else {
            alert('Unable to remove from favorites');
          }
        })
        .catch(err => {
          alert('Unable to add to favorites')
        })
      }
    }
  }
};

export const updateSavedThreshold = (threshold) => {
  return (dispatch, getState) => {
    const { id, user_id, location_id, type_id, name, value, width, dash, color } = threshold;
    updateThresholdInDatabase({id, user_id, location_id, type_id, name, value, width, dash, color})
    .then(response => {
      const { data } = response;
      alert(data.message);
    })
    .catch(err => {
      alert('Unable to update');
    })
  }
}

export const saveThreshold = (threshold, plotIndex) => {
  return (dispatch, getState) => {
    const { value, width, location_id, type_id, dash, color, name, index } = threshold;
    // console.log(threshold);
    const user_id = getState().auth.user.id;
    saveThresholdToDatabase({user_id, location_id, type_id, value, width, dash, color, name})
    .then(res => {
      const {data} = res;
      if(data.success){
        dispatch(editThreshold({saved: true, id: data.threshold.id}, plotIndex, index))
      }
      alert(data.message)
    })
    .catch(err => {
      alert('Unable to save threshold');
    })
  }
}

export const removeThresholdFromFavorites = (threshold, plotIndex) => {
  return (dispatch, getState) => {
    const { id, location_id, type_id, key } = threshold;
    const user_id = getState().auth.user.id;
    removeThresholdFromDatabse({id, user_id, location_id, type_id})
    .then(res => {
      const {data} = res;
      if(data.success){
        dispatch(removeThreshold(plotIndex, key))
      }
      alert(data.message)
    })
    .catch(err => {
      alert('Unable to remove threshold');
    })
  }
}

export const setMap = (hoverMap) => ({
  type: actions.MULTI_SET_HOVER_MAP,
  hoverMap
});

export const visited = () => ({
  type: actions.MULTI_VISIT
});

export const clearThresholds = (index) => ({
  type: actions.MULTI_CLEAR_THRESHOLDS,
  index
});

// toggle panel display
export const togglePanelDisplay = (display, index) => ({
  type: actions.MULTI_TOGGLE_SHOW_PANEL,
  display,
  index,
});

// edit chart y axis setting
export const editChartY = (changes, index) => ({
  type: actions.MULTI_EDIT_Y_AXIS,
  changes, 
  index
});

// edit the datum value
export const editDatum = (datum, index) => ({
  type: actions.MULTI_EDIT_DATUM,
  datum,
  index
});

// toggle display of datum
export const toggleDatum = (useDatum, index) => ({
  type: actions.MULTI_TOGGLE_DATUM,
  useDatum,
  index
});

// set the loading wheel display in a chart
export const setChartLoading = (loading, index) => ({
  type: actions.MULTI_SET_LOADING,
  loading, // boolean if should display loading
  index // the plot index to modify
});

// edit a lines style within a give plot
export const editChartLineStyles = (changes, index, chartKey) => ({
    type: actions.MULTI_EDIT_CHART_LINE,
    changes, // the chages to apply e.g. {width: 3}
    index, // the index of the plot e.g 0
    chartKey // the key of the line e.g. plot-1
});

// set the bulkd data for stats filter
export const setBulkStats = (bulkData, index) => ({
  type: actions.MULTI_SET_BULK,
  bulkData,
  index
});

// edit a lines deatail a give plot
export const editTrace = (changes, index, chartKey) => ({
  type: actions.MULTI_EDIT_TRACE,
  changes, // the chages to apply e.g. {visible: false}
  index, // the index of the plot e.g 0
  chartKey // the key of the line e.g. plot-1
});

// set the availalbe filters for a plot
export const setAvailableFilters = (filters, index) => ({
  type: actions.MULTI_SET_AVAILABLE,
  filters,
  index
})

// clear all the filters to null or empty array
export const clearFilters = (index) => ({
  type: actions.MULTI_CLEAR_FILTERS,
  index
});

// edit the chart layout
export const editChartLayout = (data, index) => ({
  type: actions.MULTI_EDIT_CHART_LAYOUT,
  data, // the new config items e.g. {layout: 'layout', title: 'title'}
  index // the index of the chart in plots array
});

// sets the data within the plot to be passed to plotly
export const setPlotData = (data, index) => ({
  type: actions.MULTI_SET_PLOT_DATA,
  data,
  index
});

// sets all available filters
export const setFilters = (filters) => ({
  type: actions.MULTI_SET_FILTERS,
  filters
});

// sets an error message to display to the user
export const setError = (errorMessage) => ({
  type: actions.MULTI_SET_ERROR,
  errorMessage
});

// remove the error messag from screen
export const clearError = () => ({
  type: actions.MULTI_CLEAR_ERROR,
});

// show the stats panel, also closes any other panels
export const showStats = (index) => ({
  type: actions.MULTI_SHOW_STATS,
  index
});

// toggle to show favorite locations
export const toggleFavorites = (index, bool) => ({
  type: actions.MULTI_TOGGLE_FAVORITES,
  index,
  bool
});

// open the line styles, and closes other panels
export const openLineStyles = (index) => ({
  type: actions.MULTI_OPEN_LINE_STYLES,
  index
});

// close the line styles
export const closeLineStyles = (index) => ({
  type: actions.MULTI_CLOSE_LINE_STYLES,
  index
});


// adds a new threshold to the plot at a given index
export const addThreshold = (index, data) => ({
  type: actions.MULTI_ADD_THRESHOLD,
  index,
  data
});

// removes a threshold from a plot at index with the same key
export const removeThreshold = (index, key) => ({
  type: actions.MULTI_REMOVE_THRESHOLD,
  index,
  key
});

// edit a thresholds info
export const editThreshold = (changes, plotIndex, thresholdIndex) => {
  return {
    type: actions.MULTI_EDIT_THRESHOLD,
    changes,
    plotIndex,
    thresholdIndex
  }
};

// adds a new stage to the plot at a given index
export const addStage = (index, data) => ({
  type: actions.MULTI_ADD_STAGE,
  index,
  data
});


// clear all the previous stage data
export const clearStages = (index) => ({
  type: actions.MULTI_CLEAR_STAGES,
  index
});

// remove a chart from the plot array at an index
export const removeChart = (index) => ({
  type: actions.MULTI_REMOVE_CHART,
  index
});

// adds a new chart to the beginning or end of the plots array
export const addChart = (location, origin) => ({
  type: actions.MULTI_ADD_CHART,
  location,
  origin
});

// changes to column width to 33%, 50% or 100%
export const changeColumnWidth = (width) => ({
  type: actions.MULTI_CHANGE_COL_WIDTH,
  width
});

// saves a copy of the filter to state
export const setCopyFilter = (copiedFilter) => ({
  type: actions.MULTI_COPY_FILTER,
  copiedFilter
});

// pastes the filters to a new plot
export const pasteFilter = (pasteFilter, index) => ({
  type: actions.MULTI_PASTE_FILTER,
  pasteFilter, // the filter to copy,
  index // the index to apply
});

// sets the applied filtesr for a plot
export const setAppliedFilters = (changes, index) => ({
  type: actions.MULTI_SET_APPLIED_FILTER,
  changes, // the filter to copy,
  index // the index to apply
});


// WEBPACK FOOTER //
// ./src/store/actions/multi.js